import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { EnvironmentPickerExtension } from './scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { ExplorePage } from '@backstage-community/plugin-explore';
import {apis, keycloakOIDCAuthApiRef} from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {AlertDisplay, OAuthRequestDialog, SignInPage} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import LightIcon from '@material-ui/icons/WbSunny';
import {
  GreshamHomepage,
  greshamLightTheme,
  greshamDarkTheme,
} from '@internal/plugin-gcse-ui';
import {configApiRef, useApi} from "@backstage/core-plugin-api";
import { ArgoWorkflowsPage } from '@internal/plugin-argo-workflows';
import { ApacheSparkPage } from '@internal/plugin-apache-spark';
import {
  UnifiedThemeProvider
} from "@backstage/theme";
import { TerraformPluginPage } from '@internal/plugin-terraform';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { ApprovalsPage } from '@internal/plugin-scaffolder-approval';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

const app = createApp({
  apis,
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      if (configApi.getString('auth.environment') === 'local') {
        return <SignInPage {...props} auto providers={['guest']} />;
      }
      return (
        <SignInPage
          {...props}
          provider={{
            id: 'keycloak-oidc',
            title: 'Keycloak',
            message: 'Sign in using Keycloak',
            apiRef: keycloakOIDCAuthApiRef,
          }}
        />
      );
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'gresham-light-theme',
      title: 'Light Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={greshamLightTheme} children={children} />
      ),
    },
    {
      id: 'gresham-dark-theme',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={greshamDarkTheme} children={children} />
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="home" />} />
    <Route path="/home" element={<GreshamHomepage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={
      <ScaffolderPage
        groups={[
          {
            title: 'Recommended',
            filter: (entity) => (entity?.metadata?.tags ?? []).includes('recommended'),
          },
          {
            title: 'Services',
            filter: (entity) => entity?.spec?.type === 'service',
          },
          {
            title: 'Infrastructure & GitOps',
            filter: (entity) => {
              const tags = entity?.metadata?.tags ?? [];
              return (
                entity?.spec?.type === 'infrastructure' ||
                tags.some((t: string) => ['gitops', 'argocd', 'kubernetes'].includes(t))
              );
            },
          },
          {
            title: 'All Templates',
            filter: () => true,
          },
        ]}
        headerOptions={{
          pageTitleOverride: 'Create a New Component',
          title: 'Software Templates',
          subtitle: 'Create new services, infrastructure, and GitOps configurations',
        }}
        contextMenu={{
          editor: true,
          actions: true,
          tasks: true,
        }}
      />
    }>
      <ScaffolderFieldExtensions>
        <EnvironmentPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/argo-workflows" element={<ArgoWorkflowsPage />} />
    <Route path="/apache-spark" element={<ApacheSparkPage />} />
    <Route path="/terraform" element={<TerraformPluginPage />} />
    <Route path="/rbac" element={<RbacPage />} />
    <Route path="/approvals" element={<ApprovalsPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);



